import { X } from "lucide-react";
import { IconButton, Sheet, SheetClose } from "../ui";

/**
 * CalendarDayDetail — everything known about one day, opened from a calendar row: a panel on the right on desktop
 * and a bottom sheet on phones. Focus is trapped while it is open, Escape closes it, and focus returns to the row.
 *
 * It is presentation only. `sections` are finished content from the page — `{ title?, facts?: [{ label, value }],
 * body? }` — and `actions` are the page's own buttons. It never decides who may change a day.
 */
export default function CalendarDayDetail({ open, onClose, title, subtitle, sections = [], actions, footnote }) {
  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }} title={title} titleHidden side="detail">
      <div data-ui="calendar-day-detail" className="flex min-h-0 flex-1 flex-col">
        <header className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
          <div className="min-w-0">
            <p aria-hidden="true" className="text-section-title text-foreground [text-wrap:balance]">{title}</p>
            {subtitle ? <p className="mt-0.5 text-body-sm text-secondary">{subtitle}</p> : null}
          </div>
          <SheetClose asChild>
            <IconButton icon={X} label="Close" size="sm" className="-mr-2 -mt-1" />
          </SheetClose>
        </header>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto border-t border-subtle px-5 py-4 text-body-sm">
          {sections.map((section, index) => (
            <section key={section.title || index} aria-label={section.title || undefined}>
              {section.title ? <h3 className="mb-1.5 text-label text-foreground">{section.title}</h3> : null}
              {section.facts?.length ? (
                <dl className="divide-y divide-subtle border-y border-subtle">
                  {section.facts.map((fact) => (
                    <div key={fact.label} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                      <dt className="shrink-0 text-secondary">{fact.label}</dt>
                      <dd className="min-w-0 break-words text-foreground sm:text-right">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {section.body ? <div className="space-y-2 text-secondary">{section.body}</div> : null}
            </section>
          ))}
          {footnote ? <p className="text-meta text-tertiary">{footnote}</p> : null}
        </div>
        {actions ? (
          <footer className="flex flex-col-reverse gap-2 border-t border-subtle px-5 py-3 sm:flex-row sm:justify-end">{actions}</footer>
        ) : null}
      </div>
    </Sheet>
  );
}
